// Manual do Instrutor. Markdown simples (ver components/MarkdownLite.jsx): sem crases nem ${}.
export const manualInstrutor = `# Manual do Instrutor

## 1. Visão geral

O painel do instrutor junta as tuas turmas, os teus alunos e os teus exames num só sítio. Entras com a tua conta Google e, se a tua escola te deu o papel de instrutor, abres o painel **Instrutor**.

O painel tem cinco separadores:

| Separador | Para que serve |
| --- | --- |
| Visão Geral | Os totais: número de **alunos**, de **turmas** e de **exames**, com ligações para os gerir |
| Turmas | Criar turmas, juntar alunos e escolher que disciplinas cada turma pode treinar |
| Exames | Criar, iniciar, acompanhar e corrigir exames |
| Alunos | A lista dos teus alunos e o progresso de cada um |
| Erros Frequentes | As perguntas que os alunos mais falham |

![O painel do instrutor: separadores Visão Geral, Turmas, Exames, Alunos e Erros, e os totais](/help/instrutor-visao.jpg)

No topo tens atalhos para o painel de aluno e para as outras áreas a que tens acesso. Se também fores administrador da escola, vais ver o painel de administração ao lado.

## 2. Turmas

Uma turma agrupa os alunos que fazem o mesmo curso. É a turma que decide que exames um aluno vê e que disciplinas pode treinar.

**Criar uma turma.** No separador **Turmas** ("As Minhas Turmas"), carrega em **Nova Turma** e preenche o **Nome** (por exemplo, "Turma Janeiro 2024") e, se quiseres, uma **Descrição**.

![Separador Turmas: cada turma mostra os alunos, as disciplinas desbloqueadas e os botões Disciplinas e Alunos](/help/instrutor-turmas.jpg)

**Adicionar alunos.** Dentro da turma, carrega em **Adicionar Aluno** e escreve o **e-mail do aluno**. O aluno já tem de ter entrado na plataforma e de pertencer à tua escola: não é possível adicionar quem está numa escola diferente. Enquanto um aluno não estiver numa turma, o painel dele mostra "Sem Turma Atribuída" e não vê exames.

**Escolher as disciplinas.** O botão **Disciplinas** abre "Gerir Disciplinas". Aqui escolhes as disciplinas que os alunos da turma podem **treinar**; cada uma aparece como *Desbloqueado* ou *Bloqueado*. Tens atalhos para **Desbloquear Todas** e para bloquear todas. Esta escolha **não afeta os exames**: um exame pode incluir disciplinas que a turma ainda não desbloqueou para treino.

![A janela Gerir Disciplinas: cada disciplina está Desbloqueada ou Bloqueada para a turma](/help/instrutor-disciplinas.jpg)

**Eliminar uma turma.** Os alunos não são apagados: ficam apenas sem turma e deixam de ver os exames dela.

## 3. Alunos

No separador **Alunos** vês o progresso de cada aluno e podes comparar turmas. Usa o filtro **Todas as turmas** para ver só uma.

- **Progresso dos Alunos:** a lista dos alunos com os indicadores de cada um. Se um aluno ainda não treinou, aparece "Sem dados de treino".
- **Evolução detalhada por disciplina:** abre o detalhe de um aluno, disciplina a disciplina.
- **Estado em exames:** nos exames, o painel mostra quem ainda não iniciou, quem está em progresso e quem já concluiu.

![Progresso dos Alunos: uma linha por aluno, com os indicadores por caderno, os treinos e os exames](/help/instrutor-alunos.jpg)

Os indicadores são os mesmos que o aluno vê no painel dele:

| Indicador | O que mede |
| --- | --- |
| Cobertura | A percentagem de perguntas que o aluno já viu |
| Domínio | A percentagem de perguntas que ele domina (acertou pelo menos 2 vezes, em dias diferentes) |
| Estabilidade | O quão consolidadas estão essas respostas |

O progresso final é Cobertura × Domínio × Estabilidade. A cor e o rótulo (Iniciante, A melhorar, Em progresso, Bom, Dominado, Modo manutenção) seguem os limiares do manual do aluno: verde a partir de 75%, amarelo entre 50% e 74%, laranja abaixo de 50%.

**Um aluno "todo verde" que continua a ver perguntas repetidas** não é uma falha. Quando já domina uma disciplina, o treino entra em *modo manutenção* e até 10% de cada sessão são perguntas que ele falhou nas últimas 48 horas, para as acertar de vez. As restantes variam de sessão para sessão.

### Teóricas ANAC: registo e controlo

Na lista de alunos, cada aluno tem um botão de livro (**Teóricas ANAC**). Abre o diálogo onde registas as provas teóricas desse aluno: Caderno I, Caderno II e Comunicações.

**No topo do separador Alunos** está o **Panorama ANAC**: quantos alunos concluíram as teóricas, quantos têm exame nos próximos 30 dias, quantos não têm exame marcado, quantos não têm início do curso, quantos precisam de atenção, e a lista dos exames ANAC marcados para este mês. Assim vês de relance quem vai a exame este mês.

Em cada registo do diálogo, a **época** aparece ao lado da data, em formato numérico (por exemplo «09/2026»): é o mês do exame, e vários exames no mesmo mês contam como uma só época.

**Na linha de cada aluno** vês um resumo a cores, sem abrir nada:
- **Um cartão por caderno** (Caderno I, Caderno II e Comunicações), com o estado (aprovado, marcado, reprovado, faltou, esgotado, caducado ou por fazer), a **nota** do último exame feito, e as **tentativas** usadas, de 4.
- **Sittings:** quantas épocas (meses de exame) o aluno já usou, de 6.
- **Próximo exame:** a data e os dias que faltam. A amarelo quando faltam 14 dias ou menos.
- **Avisos a vermelho:** prazo ultrapassado, novo curso. **A amarelo:** sem início do curso.

As regras que a app aplica (Regulamento INAC 164/2006, artigo 36.º; cartão de aluno, artigo 15.º):
- **Aprovado** com 75% ou mais. Abaixo disso fica **reprovado** e o aluno tem de repetir o exame.
- **Tentativas:** no máximo 4 em cada exame (Caderno I, Caderno II ou Comunicações).
- **Sittings (épocas):** no máximo 6 no total. Uma época é o **mês de exame**: vários exames no mesmo mês contam como uma só época.
- **Falta:** conta como sitting, mas não gasta tentativa.
- **Falta justificada** (atestado médico aprovado pela ANAC, com referência): não conta para nada. A referência é obrigatória.
- **Prazo de 12 meses:** as provas têm de estar aprovadas até ao último dia do 12.º mês depois do mês da primeira prova realizada. Se o prazo passar sem estarem todas aprovadas, as aprovadas caducam e o aluno tem de repetir tudo.
- **Validade para a licença:** 24 meses a contar do último dia do mês da última prova.
- **Sem sittings:** o aluno tem de fazer novo curso.

**Como registar:**
1. Regista o exame **marcado**, com a data. Um exame marcado já conta como sitting e como tentativa.
2. Depois do exame, regista a **nota** em percentagem, ou marca a falta com **Faltou** ou **Justificado**.
3. Só se apaga um exame marcado. Um exame feito fica registado.
4. Na mesma janela registas a **data do exame prático**. Só existe depois das aulas práticas, e aparece no alerta do aluno quando não há exame ANAC marcado.

### Início do curso da turma

O curso tem de estar terminado, com o exame prático, até **2 anos** depois da data de início. Essa data é da **turma** e vale para todos os alunos dela. Define-a no **formulário da turma**: ao criar a turma (**Nova turma**), ou depois, com o lápis **Editar turma** no cartão da turma. Não se define no diálogo de teóricas de um aluno. No cartão da turma, a data aparece logo abaixo do nome da turma, como «Início do curso: 01/09/2026». Para a apagar, deixa o campo vazio. O cartão de aluno não se calcula a partir desta data: a validade é a que consta no próprio cartão, guardada na ficha do aluno.

Enquanto a data não estiver definida, o cartão da turma mostra o aviso **«Falta a data de início do curso»** e a linha de cada aluno mostra **«Sem início do curso»**. Define a data assim que começar o curso.

## 4. Exames

Um exame passa por cinco passos teus: **criar**, **iniciar**, **acompanhar**, **terminar** e **desbloquear a correção**. Os alunos só veem o resultado depois do último passo.

### Criar um exame

No separador **Exames**, carrega em **Novo Exame** ("Criar Novo Exame") e preenche:

1. **Tipo de Exame:** os atalhos preenchem tudo de uma vez: *Caderno I* (50 questões, 90 min), *Caderno II* (50 questões, 90 min) ou *Comunicações* (23 questões, 30 min). Podes também configurar à mão.
2. **Título**, por exemplo "Exame Final Janeiro".
3. **Duração (min)** e **Nº Questões**. O campo indica 50–75; os atalhos do passo 1 preenchem o valor certo (por exemplo, 23 questões em Comunicações).
4. **Nível de Dificuldade**, que escolhe as perguntas pela taxa de acerto histórica:

| Nível | Fáceis | Médias | Difíceis |
| --- | --- | --- | --- |
| Aleatório (sem filtro) | sem regra | sem regra | sem regra |
| Fácil | 60% | 30% | 10% |
| Médio | 30% | 50% | 20% |
| Difícil | 10% | 40% | 50% |

5. **Conta para estatísticas e médias:** desmarca esta caixa para criar um **exame de preparação**, que não afeta as médias, o ranking nem o Skill Rating dos alunos. Usa-o para treinos e simulacros.
6. **Turma:** escolhe uma turma ou "Todas as turmas". Se escolheres uma, podes ainda **filtrar os alunos** e dar o exame só a alguns.
7. **Cadernos** e **Disciplinas** (opcionais): marca o que o exame deve incluir; se deixares vazio, entram todos.

![A janela Criar Novo Exame: tipo de exame, título, duração, número de questões e dificuldade](/help/instrutor-criar-exame.jpg)

![O resto do formulário: Conta para estatísticas e médias, turma, cadernos e disciplinas](/help/instrutor-criar-exame-2.jpg)

As perguntas ficam **fixas** quando crias o exame: todos os alunos recebem as mesmas, mas cada um vê a ordem das perguntas e das respostas baralhada.

### Os botões de cada exame

Em **Os Meus Exames**, os exames estão divididos em **Exames** e **Preparação**, e podes **filtrar por turma**. Cada exame mostra a turma, o número de disciplinas, quantos alunos já concluíram (por exemplo "1/1 concluídos") e a percentagem de aprovação.

| Estado | Botões | O que fazem |
| --- | --- | --- |
| Rascunho | **Iniciar** | Abre o exame. A plataforma pede confirmação: os alunos têm acesso imediato e o tempo começa a contar |
| Em curso | **Monitorizar**, **Terminar**, **Gerir** | *Monitorizar* mostra, em tempo real, quem não iniciou, quem está em progresso e quem concluiu. *Terminar* fecha o exame: os alunos já não podem submeter respostas |
| Concluído e Bloqueado | **Desbloquear**, **Rever Exame**, **Gerir** | Os alunos ainda não vêem a correção. *Desbloquear* liberta as revisões a todos |
| Concluído e desbloqueado | **Rever Exame**, **Gerir** | Os alunos já veem o resultado e podem rever as respostas |

O menu **⋮** de cada exame tem ainda **Renomear**, **Desbloquear Todos** e **Eliminar exame**. Em **Gerir** vês os alunos um a um: podes desbloquear a revisão de um só aluno ou eliminar o resultado de um aluno, por exemplo para ele repetir.

![Um exame Concluído e Bloqueado: os botões Desbloquear, Rever Exame e Gerir](/help/instrutor-exame-bloqueado.jpg)

### O passo que mais se esquece

Depois de **Terminar**, o exame fica "Concluído" mas **Bloqueado**: os alunos continuam a ver "A aguardar correção". Só quando carregas em **Desbloquear** passam a ver a percentagem, "Aprovado" ou "Reprovado" e a correção pergunta a pergunta. Se um aluno perguntar onde está o resultado, verifica primeiro este passo.

Um exame de preparação não mexe nas estatísticas, mas o aluno vê o resultado da mesma forma. Os exames oficiais contam para as médias, o ranking e o Skill Rating.

## 5. Erros Frequentes

Este separador mostra as **questões onde os alunos têm mais dificuldade**: as perguntas que mais se falham. É útil para decidires o que rever nas aulas.

- **Filtrar por Turma:** vê os erros de uma turma ou de "Todas as turmas".
- **Filtrar por Disciplina:** concentra a lista numa disciplina ou em "Todas as disciplinas".

![Questões Mais Erradas: filtros por turma e disciplina, e a distribuição das respostas](/help/instrutor-erros.jpg)

Uma pergunta que muitos alunos falham costuma pedir uma explicação em aula. Se a pergunta parecer errada ou mal formulada, o aluno pode reportá-la durante o treino, e a equipa da plataforma revê-a.

## 6. Problemas comuns e boas práticas

| Situação | O que verificar |
| --- | --- |
| Um aluno não vê o exame | Está na turma do exame? O exame foi atribuído a ele (se filtraste alunos)? Já carregaste em **Iniciar**? |
| Um aluno não vê o resultado | Carregaste em **Terminar** e depois em **Desbloquear**? Sem o segundo passo, o exame fica "A aguardar correção". |
| Não consigo adicionar um aluno | Ele já entrou na plataforma? Está na mesma escola que tu? O e-mail é o da conta Google que ele usa? |
| Um aluno não consegue treinar uma disciplina | Está *Bloqueada* nas disciplinas da turma. Desbloqueia-a em **Disciplinas**. |
| Um aluno precisa de repetir o exame | Em **Gerir**, elimina o resultado desse aluno. |
| Criei o exame errado | Antes de o iniciares, podes **Renomear** ou **Eliminar exame**. Depois de iniciado, **Terminar** fecha-o. |
| Um aluno aparece sem turma | Ainda não foi adicionado a nenhuma. Usa **Adicionar Aluno** com o e-mail dele. |

### Boas práticas

1. **Usa exames de preparação para ensaios.** Desmarca "Conta para estatísticas e médias" para treinos e simulacros: não sujam as médias, o ranking nem o Skill Rating.
2. **Inicia o exame só quando os alunos estiverem prontos.** O tempo conta a partir do momento em que carregas em **Iniciar**.
3. **Não te esqueças de Terminar e de Desbloquear.** São dois passos separados.
4. **Eliminar é irreversível.** Eliminar um exame apaga também todos os resultados e tentativas. Confirma duas vezes antes de o fazeres.
5. **Confirma o teu exame antes de o iniciares.** As perguntas ficam fixas quando o crias; se precisares de outras, cria um exame novo.
`;
